import React, { useCallback, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { StatCard } from "../../components/UI";
import { SearchAndFilterBar } from "../../components/search-and-filter-bar";
import ActiveFilters from "./components/ActiveFilters";
import LoadsTable from "./components/LoadsTable";
import LoadFilesModal from "./components/LoadFilesModal";
import ResponseModal from "../../components/modals/response-modal";
import EmptyState from "../../components/not-result";
import SEOHead from "../../components/SEO/SEOHead";
import loadService from "../../services/loadService";
import statsApi from "../../api/statsApi";
import Loading from "../../components/loading-animation";
import { useUser } from "../../contexts/UserContext";
import {
  canCreateLoads,
  canDeleteLoads,
  canEditLoads,
  canViewLoads,
  canDuplicateLoads,
  getUserRole
} from "../../utils/roleAccess";

const ITEMS_PER_PAGE = 10;

const normalizeAddress = (location) => {
  if (!location) return null;

  const nestedAddress =
    typeof location.address === "object" ? location.address : null;

  return {
    name: location.locationName || location.name || nestedAddress?.name || "",
    address:
      nestedAddress?.address || location.address || location.street || "",
    city: nestedAddress?.city || location.city || "",
    state: nestedAddress?.state || location.state || "",
    zip:
      nestedAddress?.zipCode ||
      nestedAddress?.zip ||
      location.zipCode ||
      location.zip ||
      "",
    contactPhone: location.contactPhone || "",
  };
};

const normalizeVehicleDetails = (load) => {
  if (load.vehicleDetails) {
    return load.vehicleDetails;
  }

  const firstVehicle = load.vehicle?.shipment?.[0];
  if (!firstVehicle) return undefined;

  return {
    vin: firstVehicle.vin,
    make: firstVehicle.make,
    model: firstVehicle.model,
    year: firstVehicle.year,
    value: firstVehicle.value,
    color: firstVehicle.color,
    mileage: firstVehicle.mileage,
  };
};

const normalizeLoad = (rawLoad = {}) => {
  const payload = rawLoad.load || rawLoad || {};

  const normalizedPickUp =
    payload.pickUpLocation || normalizeAddress(payload.pickup);
  const normalizedDelivery =
    payload.deliveryLocation || normalizeAddress(payload.delivery);

  const normalizedVehicleDetails = normalizeVehicleDetails(payload);

  const loadId = payload._id || rawLoad._id || payload.id || rawLoad.id;

  if (!loadId) {
    console.warn('Load missing ID:', rawLoad);
  }

  const normalized = {
    _id: loadId,
    id: loadId,
    orderId: payload.orderId || rawLoad.orderId || payload.billOfLadingNumber || '--',
    vin:
      payload.vin ||
      normalizedVehicleDetails?.vin ||
      payload.vehicle?.shipment?.[0]?.vin ||
      payload.freight?.shipment?.[0]?.vin ||
      "--",
    type: payload.type || rawLoad.type || { freight: false, vehicle: false },
    status: payload.status || rawLoad.status || "Listed",
    pickUpDate:
      payload.pickup?.date ||
      payload.dates?.pickupDate ||
      payload.pickUpDate ||
      rawLoad.pickUpDate,
    createdAt: payload.createdAt || rawLoad.createdAt,
    vehicleDetails: normalizedVehicleDetails,
    pickUpLocation: normalizedPickUp,
    deliveryLocation: normalizedDelivery,
    customerCompanyName:
      payload.customerCompanyName || payload.customer?.companyName,
    customerEmails: payload.customerEmails || payload.customer?.emails || [],
    carrier: payload.carrier || rawLoad.carrier,
    tracking: payload.tracking || rawLoad.tracking,
    dates: payload.dates || {
      pickupDate: payload.pickUpDate,
      deliveryDate: payload.deliveryDate,
    },
    expanded: payload.expanded ?? false,
    load: {
      ...payload,
      _id: loadId,
      id: loadId,
      orderId: payload.orderId || rawLoad.orderId || payload.billOfLadingNumber,
      type: payload.type || rawLoad.type || { freight: false, vehicle: false },
      status: payload.status || rawLoad.status || "Listed",
      pickup: payload.pickup || rawLoad.pickup,
      delivery: payload.delivery || rawLoad.delivery,
      vehicle: payload.vehicle || rawLoad.vehicle,
      freight: payload.freight || rawLoad.freight,
      carrier: payload.carrier || rawLoad.carrier,
      customer: payload.customer || rawLoad.customer,
      dates: payload.dates || rawLoad.dates,
      createdAt: payload.createdAt || rawLoad.createdAt,
    },
  };

  return normalized;
};

const LoadsPage = () => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const role = getUserRole(user);
  const hasLoadAccess = canViewLoads(role);
  const canCreate = canCreateLoads(role);
  const canEdit = canEditLoads(role);
  const canDelete = canDeleteLoads(role);
  const canDuplicate = canDuplicateLoads(role);
  const isPartner = role === 'partner';

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({}); // Store filter object
  const [selectedLoads, setSelectedLoads] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loads, setLoads] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statCards, setStatCards] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Response modal state
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    message: '',
    status: 'success',
    title: ''
  });

  // Files viewer modal state
  const [filesModal, setFilesModal] = useState({
    isOpen: false,
    load: null
  });

  const showResponseModal = (message, status = 'success', title = '') => {
    setResponseModal({
      isOpen: true,
      message,
      status,
      title
    });
  };

  const closeResponseModal = () => {
    setResponseModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleViewFiles = (load) => {
    setFilesModal({
      isOpen: true,
      load: load
    });
  };

  const closeFilesModal = () => {
    setFilesModal({ isOpen: false, load: null });
  };

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.message && location.state?.type === 'success') {
      showResponseModal(location.state.message, 'success');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Fetch statistics for stat cards
  // Fetch statistics for stat cards - only once on mount
  useEffect(() => {
    let isMounted = true;
    
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const now = new Date();
        const year = now.getFullYear();
        const from = `${year}-01-01`;
        const to = `${year}-12-31`;
        
        const response = await statsApi.getStats({ period: 'range', from, to, scopeType: 'global', grain: 'month' });
        if (!isMounted) return;
        
        const apiResponse = response?.data || response;
        const stats = apiResponse?.data || apiResponse;

        if (stats) {
          const totals = stats.totals || {};
          const breakdowns = stats.breakdowns || {};
          const loadsByStatus = breakdowns.loadsByStatus || {};
          const delivered = loadsByStatus.delivered || 0;
          const totalLoads = totals.totalLoads || 0;
          const deliveryRate = totalLoads > 0 ? Math.round((delivered / totalLoads) * 100) : 0;
          
          // Transform stats into stat cards format if needed
          const monthKey = `${year}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          const normalizeKey = (key) => (typeof key === 'string' ? key.slice(0, 7) : '');
          const points = Array.isArray(stats.points) ? stats.points : [];
          const pastAndCurrentPoints = points.filter((p) => normalizeKey(p?.periodKey) <= monthKey);
          const lastPoints = pastAndCurrentPoints.slice(-5);

          const transformedCards = [
            {
              title: 'Delivery Loads',
              value: String(totalLoads || 0),
              trend: {
                type: 'positive',
                percentage: 0,
                text: 'vs last period'
              },
              variant: 'white',
              diagramType: 'progress',
              diagramData: {
                value: deliveryRate,
                label: `${deliveryRate}%`,
                size: 60
              }
            },
            {
              title: 'Gross Revenue',
              value: `$${(totals.totalRevenue || 0).toLocaleString()}`,
              trend: {
                type: 'positive',
                percentage: 0,
                text: stats.revenueTrend?.text || 'vs last period'
              },
              variant: 'blue',
              diagramType: 'area',
              diagramData: {
                data: lastPoints.map(p => p.totals?.totalRevenue || 0),
                width: 125,
                height: 60
              }
            },
            {
              title: 'Total Profit',
              value: `$${(totals.totalProfit || 0).toLocaleString()}`,
              trend: {
                type: 'positive',
                percentage: 0,
                text: 'vs last period'
              },
              variant: 'white',
              diagramType: 'bar',
              diagramData: {
                data: lastPoints.map(p => p.totals?.totalProfit || 0),
                width: 125,
                height: 60
              }
            }
          ];
          setStatCards(transformedCards);
        }
      } catch (err) {
        // Silently handle 404 errors - endpoint may not exist yet
        if (err?.response?.status !== 404) {
          console.error("Error fetching loads stats:", err);
        }
        // Don't show modal for stats error, just log it
      } finally {
        if (isMounted) {
          setStatsLoading(false);
        }
      }
    };
    
    fetchStats();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const buildFilterParams = (values = {}) => {
    const params = {};
    const setIf = (key, value) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'string' && value.trim() === '') return;
      params[key] = value;
    };

    setIf('dates.pickupDate', values.pickupDate);
    setIf('dates.deliveryDate', values.deliveryDate);
    setIf('dates.assignedDate', values.assignedDate);
    setIf('dates.deadline', values.deadline);
    setIf('createdAt', values.creationDate);
    setIf('orderId', values.orderId);
    setIf('tracking', values.customerOrderReference);
    setIf('status', values.status);
    setIf('paymentMethod', values.paymentMethod);
    setIf('paymentTerms', values.paymentTerms);

    if (values.shipmentType === 'freight' || values.shipmentType === 'vehicle') {
      params.type = values.shipmentType;
    }
    if (values.hasTonu === 'yes') {
      params['tonu.enabled'] = true;
    } else if (values.hasTonu === 'no') {
      params['tonu.enabled'] = false;
    }

    if (values.customerCompany) {
      params.customer = values.customerCompany;
    }
    if (values.carrierCompany) {
      params.carrier = values.carrierCompany;
    }
    if (values.carrierMC && !params.carrier) {
      params.carrier = values.carrierMC;
    }

    const agentIds = [
      ...(Array.isArray(values.agentEmails) ? values.agentEmails : []),
      ...(Array.isArray(values.agentNames) ? values.agentNames : []),
    ].filter(Boolean);
    const uniqueAgentIds = [...new Set(agentIds)];
    if (uniqueAgentIds.length > 0) {
      params.createdBy = uniqueAgentIds;
    }

    if (Array.isArray(values.accountsPayableStatus) && values.accountsPayableStatus.length > 0) {
      params.accountsPayableStatus = values.accountsPayableStatus;
    }
    setIf('accountsPayableInvoicedDate', values.accountsPayableInvoicedDate);
    setIf('accountsPayablePaidDate', values.accountsPayablePaidDate);

    if (Array.isArray(values.accountsReceivableStatus) && values.accountsReceivableStatus.length > 0) {
      params.accountsReceivableStatus = values.accountsReceivableStatus;
    }
    setIf('accountsReceivableInvoicedDate', values.accountsReceivableInvoicedDate);
    setIf('accountsReceivablePaidDate', values.accountsReceivablePaidDate);

    const customerPaymentValues = [
      ...(Array.isArray(values.customerPaymentStatus) ? values.customerPaymentStatus : []),
    ].filter(Boolean);
    if (customerPaymentValues.length > 0) {
      params.customerPaymentStatus = customerPaymentValues;
    }

    return params;
  };

  const fetchLoads = useCallback(async () => {
    setLoading(true);
    setFetchError(null);

    try {
      if (isPartner && !searchTerm.trim() && Object.keys(filterValues).length === 0) {
        setLoads([]);
        setTotalItems(0);
        setLoading(false);
        return;
      }

      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const filterParams = buildFilterParams(filterValues);
      const hasFilters = Object.keys(filterParams).length > 0;
      Object.assign(params, filterParams);

      const response = (searchTerm.trim() || hasFilters)
        ? await loadService.searchLoads(searchTerm.trim(), params)
        : await loadService.getLoads(params);
      const apiResponse = response?.data || response;
      const rawLoads = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
      const pagination = apiResponse?.pagination || apiResponse?.meta || {};

      if (!Array.isArray(rawLoads) || rawLoads.length === 0) {
        console.warn('No loads found in API response');
        setLoads([]);
        setTotalItems(0);
        return;
      }

      const normalizedLoads = rawLoads.map((load) => normalizeLoad(load));
      const total = pagination.total ?? pagination.totalItems ?? rawLoads.length;
      const totalPages =
        pagination.totalPages ??
        (total > 0 ? Math.ceil(total / ITEMS_PER_PAGE) : 1);

      if (totalPages > 0 && currentPage > totalPages) {
        setCurrentPage(totalPages);
        return;
      }

      setLoads(normalizedLoads);
      setTotalItems(total);
    } catch (err) {
      console.error("Error fetching loads:", err);
      setFetchError(err?.response?.data?.error || err?.message || "Failed to fetch loads");
      setLoads([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterValues, isPartner]);

  useEffect(() => {
    if (!hasLoadAccess) return;
    fetchLoads();
  }, [currentPage, searchTerm, filterValues, fetchLoads, hasLoadAccess]);

  const handleFilterApply = (filters) => {
    setFilterValues(filters);
    
    // Convert filter object to array of filter strings for ActiveFilters display
    const filterArray = [];
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== '' && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) {
          filterArray.push(`${key}: ${value.join(', ')}`);
        } else if (typeof value === 'boolean' && value) {
          filterArray.push(key);
        } else if (typeof value === 'string' && value.trim() !== '') {
          filterArray.push(`${key}: ${value}`);
        }
      }
    });
    setActiveFilters(filterArray);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterIndex) => {
    const filterToRemove = activeFilters[filterIndex];
    const newFilters = activeFilters.filter(
      (_, index) => index !== filterIndex
    );
    setActiveFilters(newFilters);
    const updatedFilterValues = { ...filterValues };
    const key = filterToRemove.split(':')[0].trim();
    if (updatedFilterValues[key] !== undefined) {
      if (Array.isArray(updatedFilterValues[key])) {
        updatedFilterValues[key] = [];
      } else {
        updatedFilterValues[key] = '';
      }
      setFilterValues(updatedFilterValues);
    }
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
    setFilterValues({});
    setCurrentPage(1);
  };

  const handleSelectAll = (pageItemIds = []) => {
    if (pageItemIds.length === 0) {
      setSelectedLoads([]);
      return;
    }

    const hasSelectedAll = pageItemIds.every((id) =>
      selectedLoads.includes(id)
    );

    if (hasSelectedAll) {
      setSelectedLoads((prev) => prev.filter((id) => !pageItemIds.includes(id)));
    } else {
      setSelectedLoads((prev) => {
        const merged = new Set(prev);
        pageItemIds.forEach((id) => merged.add(id));
        return Array.from(merged);
      });
    }
  };

  const handleSelectLoad = (loadId) => {
    setSelectedLoads((prev) =>
      prev.includes(loadId)
        ? prev.filter((id) => id !== loadId)
        : [...prev, loadId]
    );
  };

  const handleDeleteSelected = async (idsToDelete) => {
    if (!canDelete) return;
    const targetIds =
      Array.isArray(idsToDelete) && idsToDelete.length > 0
        ? idsToDelete
        : selectedLoads;

    if (targetIds.length === 0) return;

    try {
      if (targetIds.length === 1) {
        await loadService.deleteLoad(targetIds[0]);
      } else if (loadService.bulkDeleteLoads) {
        await loadService.bulkDeleteLoads(targetIds);
      } else {
        await Promise.all(targetIds.map((id) => loadService.deleteLoad(id)));
      }
      setSelectedLoads((prev) =>
        prev.filter((id) => !targetIds.includes(id))
      );
      showResponseModal(
        `Successfully deleted ${targetIds.length} load(s)`,
        'success',
        'Deleted'
      );
      fetchLoads();
    } catch (err) {
      console.error("Error deleting loads:", err);
      showResponseModal(
        err?.response?.data?.error || err?.message || "Failed to delete loads",
        'error',
        'Delete Failed'
      );
    }
  };

  const handleToggleExpand = (loadId) => {
    setLoads((prev) =>
      prev.map((load) =>
        (load._id || load.id) === loadId
          ? { ...load, expanded: !load.expanded }
          : load
      )
    );
  };

  const handleAddNewLoad = () => {
    if (!canCreate) return;
    navigate("/loads/add");
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  if (!hasLoadAccess) {
    return (
      <Container fluid style={{ backgroundColor: colors.background, minHeight: "100vh" }}>
        <EmptyState
          variant="error"
          title="Access denied"
          message="You do not have access to loads."
        />
      </Container>
    );
  }

  return (
    <>
      <SEOHead
        title="Load Management"
        description="Manage and track all your freight loads efficiently. View, create, edit, and monitor load status in real-time. Comprehensive load management system for logistics operations."
        keywords="load management, freight tracking, shipment tracking, logistics loads, freight management, load tracking, transportation loads"
        url="/loads"
      />
      <Container
        fluid
        style={{ backgroundColor: colors.background, minHeight: "100vh" }}
      >
      {statCards.length > 0 && (
        <div className="stat-cards">
          {statCards.map((card, index) => (
            <div className="stat-cards__wrapper" key={index}>
              <StatCard {...card} />
            </div>
          ))}
        </div>
      )}

      <SearchAndFilterBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onFilterApply={handleFilterApply}
        onAddNewClick={handleAddNewLoad}
        pageType="loads"
        disableAddModal={true}
        showAddButton={canCreate}
        initialFilters={filterValues}
      />

      {activeFilters.length > 0 && (
        <ActiveFilters
          filters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />
      )}

      {loading && loads.length === 0 ? (
        <Loading size="large" />
      ) : fetchError ? (
        <EmptyState
          variant="error"
          title="Failed to load data"
          message={fetchError}
          onRetry={fetchLoads}
          retryText="Retry"
        />
      ) : loads.length === 0 ? (
        <EmptyState
          variant={searchTerm ? "search" : "empty"}
          title={searchTerm ? "No results found" : "No loads yet"}
          message={
            searchTerm
              ? `No loads matching "${searchTerm}"`
              : isPartner
              ? "Use search to find loads by Order ID."
              : canCreate
              ? "Create your first load to get started"
              : "No loads available"
          }
        />
      ) : (
        <LoadsTable
          loads={loads}
          selectedLoads={selectedLoads}
          onSelectAll={handleSelectAll}
          onSelectLoad={handleSelectLoad}
          onToggleExpand={handleToggleExpand}
          onDeleteSelected={handleDeleteSelected}
          onEdit={canEdit ? (load) => navigate(`/loads/edit/${load._id || load.id}`) : undefined}
          onViewFiles={handleViewFiles}
          canSelect={canDelete}
          canDelete={canDelete}
          canEdit={canEdit}
          canDuplicate={canDuplicate}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          isServerPaginated
          loading={loading}
        />
      )}

      <ResponseModal
        isOpen={responseModal.isOpen}
        message={responseModal.message}
        status={responseModal.status}
        title={responseModal.title}
        onClose={closeResponseModal}
      />

      <LoadFilesModal
        isOpen={filesModal.isOpen}
        onClose={closeFilesModal}
        load={filesModal.load}
      />
    </Container>
    </>
  );
};

export default LoadsPage;
